import { CategoryBadge } from "@gromia/components/blog/Badges";
import * as s from "@gromia/components/theme/t.module.css";
import { LinkArrow } from "@gromia/components/ui/LinkArrow";
import { gray1x1 } from "@gromia/utils/base64pixels";
import { formatDate } from "@gromia/utils/format";
import {
  pathToPostPage,
  removeHtmlTags,
  WpPostT,
} from "@gromia/utils/models/blog";
import cx from "classnames";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import { Link } from "gatsby";
import * as React from "react";

type Props = {
  post: WpPostT;
};

export function PostLinkCard(props: Props) {
  const post = props.post;
  const url = pathToPostPage(post);

  return (
    <div className={cx(s.dFlex, s.flexColumn, s.flexSmRow)}>
      <div className={cx(s.bgWhite, s.prSm6)}>
        <div
          className={cx(
            s.dFlex,
            s.justifyContentStart,
            s.alignItemsStart,
            s.alignItemsSmCenter,
            s.flexColumn,
            s.flexSmRow,
          )}
        >
          <div className={cx(s.mr4)}>
            {pipe(
              post.categories.nodes,
              A.head,
              O.map(cat => <CategoryBadge category={cat} />),
              O.toNullable,
            )}
          </div>
          <div
            className={cx(
              s.dBlock,
              s.dSmNone,
              s.flexGrow1,
              s.bgCover,
              s.bgImgHeroBottom,
              s.rounded,
              s.mt3,
            )}
            style={{
              minHeight: 134,
              minWidth: "100%",
              backgroundImage: `url(${
                post.featuredImageOptim?.medium ?? gray1x1
              })`,
            }}
          />
          <div className={cx(s.fontSizeSmall1, s.textRight, s.mt3, s.mtSm0)}>
            <PostLinkInfo post={post} />
          </div>
        </div>
        <Link to={url} className={cx(s.textBody, s.textDecorationNone)}>
          <h2
            className={cx(
              s.fontSizeLarge1,
              s.fontWeightBold,
              s.mb2,
              s.mt2,
              s.mtSm3,
            )}
          >
            {post.title}
          </h2>
        </Link>
        <div
          className={cx(s.dNone, s.dMdBlock, s.fontSizeSmall1, s.spaceBottom1)}
        >
          <Link
            to={url}
            className={cx(s.lineClamp4, s.textBody, s.textDecorationNone)}
          >
            {removeHtmlTags(post.excerpt)}
          </Link>
        </div>
        <LinkArrow to={url} className={cx(s.fontSizeSmall1)}>
          Leggi l'articolo
        </LinkArrow>
      </div>
      <Link
        to={url}
        className={cx(
          s.dNone,
          s.dSmBlock,
          s.flexGrow1,
          s.bgCover,
          s.bgImgHeroBottom,
          s.rounded,
        )}
        style={{
          minHeight: 240,
          minWidth: 280,
          backgroundImage: `url(${post.featuredImageOptim?.small ?? gray1x1})`,
        }}
      />
    </div>
  );
}

export function PostLinkThumb(props: Props) {
  const post = props.post;
  const url = pathToPostPage(post);
  return (
    <div>
      <div className={cx(s.dFlex)}>
        <div className={cx(s.dFlex, s.flexColumn)}>
          <span className={cx(s.fontSizeSmall1)}>
            <PostLinkInfo post={post} />
          </span>
          <Link
            to={url}
            className={cx(
              s.textBody,
              s.textDecorationNone,
              s.fontWeightBold,
              s.fontSizeBase,
              s.overflowHidden,
              s.lineClamp3,
            )}
          >
            {post.title}
          </Link>
          <LinkArrow to={url} className={cx(s.mt2, s.fontSizeSmall1)}>
            Leggi l'articolo
          </LinkArrow>
        </div>
      </div>
    </div>
  );
}

export function PostLinkInfo(props: Props) {
  return (
    <span>
      {formatDate(new Date(props.post.date))}
      <span className={cx(s.ml3)}>
        <ReadingTime post={props.post} />
      </span>
    </span>
  );
}

function ReadingTime(props: Props) {
  const words = props.post.content.split(" ").length;

  return (
    <React.Fragment>
      <b>{Math.round(words / 200.0)}</b> min. di lettura
    </React.Fragment>
  );
}
